// src/pages/DeliveryRatePage.jsx
import { NavLink } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import "./DeliveryRatePage.css";

const emptyForm = {
  stateDivision: "",
  stateDivisionMm: "",

  fromCity: "Yangon",
  fromCityMm: "ရန်ကုန်",

  toCity: "",
  toCityMm: "",

  zone: "",
  fromKg: 0.1,
  toKg: 3,
  basePrice: 0,
  nextKgPrice: 0,
  active: true,
};

export default function DeliveryRatePage() {
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadRates = async () => {
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/delivery-rates`);
      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.success) {
        alert(json?.message || "Delivery rates load failed");
        setRates([]);
        return;
      }

      setRates(json.rates || []);
    } catch (err) {
      console.error("Load delivery rates error:", err);
      alert("Delivery rates load error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRates();
  }, []);

  const updateForm = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.fromCity.trim()) {
      alert("From City လိုပါတယ်");
      return;
    }

    if (!form.toCity.trim()) {
      alert("To City လိုပါတယ်");
      return;
    }

    if (Number(form.toKg || 0) <= 0) {
      alert("To KG must be greater than 0");
      return;
    }

    setSaving(true);

    const payload = {
      ...form,
      fromKg: Number(form.fromKg || 0),
      toKg: Number(form.toKg || 0),
      basePrice: Number(form.basePrice || 0),
      nextKgPrice: Number(form.nextKgPrice || 0),
      active: form.active === true,
    };

    try {
      const url = editingId
        ? `${API_BASE}/api/delivery-rates/${encodeURIComponent(editingId)}`
        : `${API_BASE}/api/delivery-rates`;

      const method = editingId ? "PATCH" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.success) {
        alert(json?.message || "Save failed");
        return;
      }

      alert(editingId ? "✅ Delivery rate updated" : "✅ Delivery rate added");
      resetForm();
      await loadRates();
    } catch (err) {
      console.error("Save delivery rate error:", err);
      alert("Save delivery rate error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (rate) => {
    setEditingId(rate.id);

    setForm({
      stateDivision: rate.stateDivision || "",
      stateDivisionMm: rate.stateDivisionMm || "",

      fromCity: rate.fromCity || "",
      fromCityMm: rate.fromCityMm || "",

      toCity: rate.toCity || "",
      toCityMm: rate.toCityMm || "",

      zone: rate.zone || "",
      fromKg: Number(rate.fromKg || 0),
      toKg: Number(rate.toKg || 0),
      basePrice: Number(rate.basePrice || 0),
      nextKgPrice: Number(rate.nextKgPrice || 0),
      active: rate.active !== false,
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (rate) => {
    const ok = window.confirm(
      `Delete delivery rate?\n\n${rate.fromCity} → ${rate.toCity}`,
    );

    if (!ok) return;

    try {
      const res = await fetch(
        `${API_BASE}/api/delivery-rates/${encodeURIComponent(rate.id)}`,
        {
          method: "DELETE",
        },
      );

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.success) {
        alert(json?.message || "Delete failed");
        return;
      }

      alert("✅ Deleted");
      await loadRates();
    } catch (err) {
      console.error("Delete delivery rate error:", err);
      alert("Delete delivery rate error");
    }
  };

  const handleToggleActive = async (rate) => {
    const payload = {
      stateDivision: rate.stateDivision || "",
      fromCity: rate.fromCity || "",
      toCity: rate.toCity || "",
      zone: rate.zone || "",
      fromKg: Number(rate.fromKg || 0),
      toKg: Number(rate.toKg || 0),
      basePrice: Number(rate.basePrice || 0),
      nextKgPrice: Number(rate.nextKgPrice || 0),
      active: rate.active === false,
    };

    try {
      const res = await fetch(
        `${API_BASE}/api/delivery-rates/${encodeURIComponent(rate.id)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.success) {
        alert(json?.message || "Update failed");
        return;
      }

      await loadRates();
    } catch (err) {
      console.error("Toggle active error:", err);
      alert("Toggle active error");
    }
  };

  const handleDownloadTemplate = () => {
    const rows = [
      {
        stateDivision: "MDY",
        fromCity: "Yangon",
        toCity: "Mandalay",
        zone: "B",
        fromKg: 0.1,
        toKg: 3,
        basePrice: 4500,
        nextKgPrice: 1800,
        active: true,
      },
      {
        stateDivision: "MGY",
        fromCity: "Yangon",
        toCity: "Magway",
        zone: "B",
        fromKg: 0.1,
        toKg: 3,
        basePrice: 4500,
        nextKgPrice: 1800,
        active: true,
      },
    ];

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Delivery Rates");
    XLSX.writeFile(wb, "delivery-rate-template.xlsx");
  };

  const normalizeExcelValue = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    const ok = window.confirm(
      "Upload delivery rates from Excel?\nExisting same From City + To City + KG range will be replaced.",
    );

    if (!ok) return;

    setSaving(true);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const rows = XLSX.utils.sheet_to_json(sheet, {
        defval: "",
      });

      if (!rows.length) {
        alert("Excel file ထဲမှာ data မရှိပါ");
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const row of rows) {
        const payload = {
          stateDivision: normalizeExcelValue(
            row.stateDivision || row["State / Division"] || row["State"],
          ),

          stateDivisionMm: normalizeExcelValue(
            row.stateDivisionMm ||
              row["State Myanmar"] ||
              row["State / Division Myanmar"] ||
              row["State MM"],
          ),

          fromCity: normalizeExcelValue(row.fromCity || row["From City"]),

          fromCityMm: normalizeExcelValue(
            row.fromCityMm || row["From City Myanmar"] || row["From City MM"],
          ),

          toCity: normalizeExcelValue(row.toCity || row["To City"]),

          toCityMm: normalizeExcelValue(
            row.toCityMm || row["To City Myanmar"] || row["To City MM"],
          ),

          zone: normalizeExcelValue(row.zone || row["Zone"]),
          fromKg: Number(row.fromKg || row["From KG"] || 0.1),
          toKg: Number(row.toKg || row["To KG"] || 3),
          basePrice: Number(
            row.basePrice || row["Base Price"] || row.price || 0,
          ),
          nextKgPrice: Number(row.nextKgPrice || row["Next 1 KG"] || 0),
          active:
            row.active === false ||
            String(row.active || "").toLowerCase() === "false"
              ? false
              : true,
        };

        if (!payload.fromCity || !payload.toCity || payload.toKg <= 0) {
          failCount += 1;
          continue;
        }

        try {
          const res = await fetch(`${API_BASE}/api/delivery-rates`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });

          const json = await res.json().catch(() => ({}));

          if (!res.ok || !json?.success) {
            failCount += 1;
          } else {
            successCount += 1;
          }
        } catch {
          failCount += 1;
        }
      }

      alert(
        `✅ Upload finished\nSuccess: ${successCount}\nFailed: ${failCount}`,
      );
      await loadRates();
    } catch (err) {
      console.error("Excel upload error:", err);
      alert("Excel upload error");
    } finally {
      setSaving(false);
    }
  };

  const filteredRates = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return rates;

    return rates.filter((r) => {
      return [
        r.stateDivision,
        r.fromCity,
        r.toCity,
        r.zone,
        r.basePrice,
        r.nextKgPrice,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [rates, search]);

  return (
    <Layout title="Delivery Rate Management">
      <div className="delivery-page">
        <div className="delivery-tabs">
          <NavLink to="/erp/delivery-rates">Delivery Rates</NavLink>
          <NavLink to="/erp/delivery-free-rules">Free Shipping Rules</NavLink>
        </div>
        <div className="delivery-header">
          <div>
            <h2>Delivery Rate Management</h2>
            <p>
              From City / To City / KG အလိုက် delivery price သတ်မှတ်ပြီး POS မှာ
              auto delivery charge ထည့်နိုင်ရန်။
            </p>
          </div>

          <div className="delivery-header-actions">
            <button
              type="button"
              className="delivery-template-btn"
              onClick={handleDownloadTemplate}
            >
              Download Template
            </button>

            <label className="delivery-upload-btn">
              Upload Excel
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleExcelUpload}
                hidden
              />
            </label>
          </div>
        </div>

        <form className="delivery-form-card" onSubmit={handleSubmit}>
          <h3>{editingId ? "Edit Delivery Rate" : "Add Delivery Rate"}</h3>

          <div className="delivery-form-grid">
            <div className="delivery-field">
              <label>State / Division</label>
              <input
                value={form.stateDivision}
                onChange={(e) => updateForm("stateDivision", e.target.value)}
                placeholder="MDY"
              />
            </div>

            <div className="delivery-field">
              <label>From City</label>
              <input
                value={form.fromCity}
                onChange={(e) => updateForm("fromCity", e.target.value)}
                placeholder="Yangon"
              />
            </div>

            <div className="delivery-field">
              <label>To City / Township</label>
              <input
                value={form.toCity}
                onChange={(e) => updateForm("toCity", e.target.value)}
                placeholder="Mandalay"
              />
            </div>

            <div className="delivery-field">
              <label>Zone</label>
              <input
                value={form.zone}
                onChange={(e) => updateForm("zone", e.target.value)}
                placeholder="B"
              />
            </div>

            <div className="delivery-field">
              <label>From KG</label>
              <input
                type="number"
                step="0.1"
                value={form.fromKg}
                onChange={(e) => updateForm("fromKg", e.target.value)}
              />
            </div>

            <div className="delivery-field">
              <label>To KG</label>
              <input
                type="number"
                step="0.1"
                value={form.toKg}
                onChange={(e) => updateForm("toKg", e.target.value)}
              />
            </div>

            <div className="delivery-field">
              <label>Base Price</label>
              <input
                type="number"
                value={form.basePrice}
                onChange={(e) => updateForm("basePrice", e.target.value)}
                placeholder="4500"
              />
            </div>

            <div className="delivery-field">
              <label>Next 1 KG Price</label>
              <input
                type="number"
                value={form.nextKgPrice}
                onChange={(e) => updateForm("nextKgPrice", e.target.value)}
                placeholder="1800"
              />
            </div>
          </div>

          <label className="delivery-check">
            <input
              type="checkbox"
              checked={form.active === true}
              onChange={(e) => updateForm("active", e.target.checked)}
            />
            Active
          </label>

          <div className="delivery-form-actions">
            <button
              type="submit"
              className="delivery-save-btn"
              disabled={saving}
            >
              {saving ? "Saving..." : editingId ? "Update Rate" : "Add Rate"}
            </button>

            {editingId && (
              <button
                type="button"
                className="delivery-cancel-btn"
                onClick={resetForm}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>

        <div className="delivery-list-card">
          <div className="delivery-list-header">
            <div>
              <h3>Delivery Rate List</h3>
              <p>Total: {filteredRates.length}</p>
            </div>

            <input
              className="delivery-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search city / zone / price..."
            />
          </div>

          {loading ? (
            <div className="delivery-loading">Loading...</div>
          ) : (
            <div className="delivery-table-wrap">
              <table className="delivery-table">
                <thead>
                  <tr>
                    <th>State</th>
                    <th>From City</th>
                    <th>To City</th>
                    <th>Zone</th>
                    <th>KG Range</th>
                    <th>Base Price</th>
                    <th>Next 1 KG</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRates.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="delivery-empty">
                        No delivery rates found
                      </td>
                    </tr>
                  ) : (
                    filteredRates.map((rate) => (
                      <tr key={rate.id}>
                        <td>
                          <strong>{rate.stateDivision || "-"}</strong>
                          {rate.stateDivisionMm && (
                            <div className="delivery-mm-text">
                              {rate.stateDivisionMm}
                            </div>
                          )}
                        </td>

                        <td>
                          <strong>{rate.fromCity || "-"}</strong>
                          {rate.fromCityMm && (
                            <div className="delivery-mm-text">
                              {rate.fromCityMm}
                            </div>
                          )}
                        </td>

                        <td>
                          <strong>{rate.toCity || "-"}</strong>
                          {rate.toCityMm && (
                            <div className="delivery-mm-text">
                              {rate.toCityMm}
                            </div>
                          )}
                        </td>

                        <td>{rate.zone || "-"}</td>
                        <td>
                          {Number(rate.fromKg || 0)} KG →{" "}
                          {Number(rate.toKg || 0)} KG
                        </td>
                        <td>
                          {Number(rate.basePrice || 0).toLocaleString()} Ks
                        </td>
                        <td>
                          {Number(rate.nextKgPrice || 0).toLocaleString()} Ks
                        </td>
                        <td>
                          <span
                            className={
                              rate.active
                                ? "delivery-status active"
                                : "delivery-status inactive"
                            }
                          >
                            {rate.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="delivery-actions">
                            <button
                              type="button"
                              className="delivery-edit-btn"
                              onClick={() => handleEdit(rate)}
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className={
                                rate.active
                                  ? "delivery-toggle-btn danger"
                                  : "delivery-toggle-btn"
                              }
                              onClick={() => handleToggleActive(rate)}
                            >
                              {rate.active ? "Disable" : "Enable"}
                            </button>

                            <button
                              type="button"
                              className="delivery-delete-btn"
                              onClick={() => handleDelete(rate)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
